import { FormEvent, useEffect, useState } from "react";
import { AlertTriangle, CalendarDays, Check, Clock, LogOut, Mail, ReceiptText, ShieldCheck, Tag, UserRound, X } from "lucide-react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/motion/Reveal";
import { useAuth } from "../context/AuthContext";
import type { AccountBooking } from "../context/AuthContext";
import { siteInfo } from "../data/site";

const bookingTimeZone = "America/New_York";

function money(value: number | null | undefined) {
  if (typeof value !== "number") {
    return "Quote pending";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateOnly: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${dateOnly}T00:00:00.000Z`));
}

function todayDate() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: bookingTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function bookingStatusLabel(booking: AccountBooking) {
  if (booking.status === "hold" && booking.holdExpiresAt) {
    return "Review hold";
  }

  return booking.status;
}

function formatDateTime(value?: string) {
  if (!value) {
    return "";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: bookingTimeZone,
    timeZoneName: "short",
  }).format(new Date(value));
}

function bookingReference(booking: AccountBooking) {
  return booking.reference || booking.id.slice(0, 8).toUpperCase();
}

function bookingStatusDescription(booking: AccountBooking) {
  if (booking.status === "hold" && booking.holdExpiresAt) {
    return `Held until ${formatDateTime(booking.holdExpiresAt)} while we review your request.`;
  }

  if (booking.status === "confirmed") {
    return "Confirmed stay.";
  }

  if (booking.status === "cancelled") {
    return "Cancelled stay.";
  }

  return "";
}

function canCancelBooking(booking: AccountBooking) {
  return booking.status !== "cancelled" && booking.start > todayDate();
}

function cancellationCopy(booking: AccountBooking) {
  if (booking.status === "confirmed") {
    return {
      title: `Cancel confirmed stay?`,
      body: `${formatDate(booking.start)} to ${formatDate(booking.end)} will be released, the owner will be notified, and a cancellation email will be sent.`,
      button: "Yes, cancel stay",
    };
  }

  return {
    title: "Cancel this request?",
    body: `${formatDate(booking.start)} to ${formatDate(booking.end)} will be released from review hold, the owner will be notified, and a cancellation email will be sent.`,
    button: "Yes, cancel request",
  };
}

function dateValue(dateOnly: string) {
  return new Date(`${dateOnly}T00:00:00.000Z`).valueOf();
}

function createdValue(booking: AccountBooking) {
  return booking.createdAt ? new Date(booking.createdAt).valueOf() : 0;
}

function dateTimeValue(value?: string) {
  if (!value) {
    return Number.MAX_SAFE_INTEGER;
  }

  const parsed = new Date(value).valueOf();
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

function pluralStay(count: number) {
  return count === 1 ? "stay" : "stays";
}

function AccountDashboardSummary({ bookings, marketingOptIn }: { bookings: AccountBooking[]; marketingOptIn: boolean }) {
  const today = todayDate();
  const activeBookings = bookings.filter((booking) => booking.status !== "cancelled");
  const upcomingBookings = activeBookings
    .filter((booking) => booking.end >= today)
    .sort((left, right) => dateValue(left.start) - dateValue(right.start) || createdValue(right) - createdValue(left));
  const nextBooking = upcomingBookings[0] ?? null;
  const holdBookings = upcomingBookings
    .filter((booking) => booking.status === "hold")
    .sort((left, right) => dateTimeValue(left.holdExpiresAt) - dateTimeValue(right.holdExpiresAt));
  const confirmedBookings = upcomingBookings.filter((booking) => booking.status === "confirmed");
  const nextHold = holdBookings[0] ?? null;

  return (
    <section className="account-dashboard-summary" aria-label="Account stay summary">
      <div className="account-summary-item">
        <CalendarDays size={21} aria-hidden="true" />
        <div>
          <span>Next stay</span>
          <strong>{nextBooking ? formatDate(nextBooking.start) : "None scheduled"}</strong>
          <small>{nextBooking ? `${nextBooking.apartmentTitle} · ${bookingStatusLabel(nextBooking)}` : "Browse apartments when you are ready."}</small>
        </div>
      </div>
      <div className="account-summary-item">
        <ReceiptText size={21} aria-hidden="true" />
        <div>
          <span>Saved stays</span>
          <strong>{activeBookings.length}</strong>
          <small>
            {activeBookings.length} active {pluralStay(activeBookings.length)} in this account.
          </small>
        </div>
      </div>
      <div className="account-summary-item">
        <Clock size={21} aria-hidden="true" />
        <div>
          <span>Review holds</span>
          <strong>{holdBookings.length}</strong>
          <small>{nextHold?.holdExpiresAt ? `Next hold expires ${formatDateTime(nextHold.holdExpiresAt)}.` : "No requests waiting on owner review."}</small>
        </div>
      </div>
      <div className="account-summary-item">
        <Mail size={21} aria-hidden="true" />
        <div>
          <span>Email offers</span>
          <strong>{marketingOptIn ? "On" : "Off"}</strong>
          <small>{marketingOptIn ? "Discounts and stay updates are enabled." : "Turn on discounts and updates in your profile."}</small>
        </div>
      </div>
      {confirmedBookings.length > 0 && (
        <div className="account-summary-note">
          <Check size={17} aria-hidden="true" />
          <span>
            {confirmedBookings.length} upcoming confirmed {pluralStay(confirmedBookings.length)}. Keep your phone number current so arrival details reach you.
          </span>
        </div>
      )}
    </section>
  );
}

function StatusMessage({ tone, children }: { tone: "success" | "error"; children: string }) {
  return (
    <div className={`account-message account-message--${tone}`} role="status" aria-live="polite">
      {tone === "success" ? <Check size={18} aria-hidden="true" /> : <X size={18} aria-hidden="true" />}
      <span>{children}</span>
    </div>
  );
}

type AccountLocationState = {
  bookingAccountPrefill?: {
    name?: string;
    email?: string;
    phone?: string;
  };
} | null;

function BookingList({
  bookings,
  cancellingBookingId,
  onCancel,
}: {
  bookings: AccountBooking[];
  cancellingBookingId: string;
  onCancel: (booking: AccountBooking) => Promise<void>;
}) {
  const [confirmingBookingId, setConfirmingBookingId] = useState("");
  const today = todayDate();
  const upcomingBookings = bookings
    .filter((booking) => booking.status !== "cancelled" && booking.end >= today)
    .sort((left, right) => dateValue(left.start) - dateValue(right.start) || createdValue(right) - createdValue(left));
  const pastBookings = bookings
    .filter((booking) => booking.status !== "cancelled" && booking.end < today)
    .sort((left, right) => dateValue(right.start) - dateValue(left.start) || createdValue(right) - createdValue(left));
  const cancelledBookings = bookings
    .filter((booking) => booking.status === "cancelled")
    .sort((left, right) => dateValue(right.start) - dateValue(left.start) || createdValue(right) - createdValue(left));
  const sections = [
    { id: "upcoming", title: "Upcoming", bookings: upcomingBookings },
    { id: "past", title: "Past stays", bookings: pastBookings },
    { id: "cancelled", title: "Cancelled", bookings: cancelledBookings },
  ].filter((section) => section.bookings.length > 0);

  if (bookings.length === 0) {
    return (
      <div className="account-empty-state">
        <CalendarDays size={28} aria-hidden="true" />
        <h3>No saved stays yet</h3>
        <p>Reserved stays will appear here after a booking request is submitted while signed in.</p>
        <Link className="button button--secondary" to="/apartments">
          View apartments
        </Link>
      </div>
    );
  }

  const renderBooking = (booking: AccountBooking) => {
    const cancelable = canCancelBooking(booking);
    const isCancelling = cancellingBookingId === booking.id;
    const confirming = confirmingBookingId === booking.id;
    const statusDescription = bookingStatusDescription(booking);
    const cancelAction = cancellationCopy(booking);

    return (
      <article className="account-booking-card" key={booking.id}>
        <div className="account-booking-card__main">
          <div className="account-booking-card__meta">
            <span className={`account-pill account-pill--${booking.status}`}>{bookingStatusLabel(booking)}</span>
            <span className="account-reference-pill">
              <ReceiptText size={14} aria-hidden="true" />
              Ref {bookingReference(booking)}
            </span>
          </div>
          <h3>{booking.apartmentTitle}</h3>
          <p>
            {formatDate(booking.start)} to {formatDate(booking.end)} · {booking.guests} guest{booking.guests === 1 ? "" : "s"}
          </p>
          {statusDescription && (
            <p className="account-booking-card__status">
              {booking.status === "hold" && <Clock size={15} aria-hidden="true" />}
              {statusDescription}
            </p>
          )}
          {booking.note && <p className="account-booking-note">{booking.note}</p>}
        </div>
        <div className="account-booking-card__price">
          <strong>{money(booking.total)}</strong>
          <span>estimated total</span>
        </div>
        <div className="account-booking-card__actions">
          <Link className="text-link" to={`/apartments/${booking.apartmentSlug}`}>
            View stay
          </Link>
          <Link className="text-link" to="/contact">
            Contact
          </Link>
          {cancelable && (
            <button className="account-action-link account-action-link--danger" type="button" disabled={isCancelling} onClick={() => setConfirmingBookingId(booking.id)}>
              <X size={15} aria-hidden="true" />
              {isCancelling ? "Cancelling" : booking.status === "confirmed" ? "Cancel stay" : "Cancel request"}
            </button>
          )}
        </div>
        {confirming && (
          <div className="account-cancel-confirmation" role="alert">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>{cancelAction.title}</strong>
              <span>{cancelAction.body}</span>
            </div>
            <div className="account-cancel-confirmation__actions">
              <button
                className="button button--light"
                type="button"
                disabled={isCancelling}
                onClick={() => {
                  void onCancel(booking).then(() => setConfirmingBookingId(""));
                }}
              >
                {isCancelling ? "Cancelling" : cancelAction.button}
              </button>
              <button className="account-action-link" type="button" disabled={isCancelling} onClick={() => setConfirmingBookingId("")}>
                Keep stay
              </button>
            </div>
          </div>
        )}
      </article>
    );
  };

  return (
    <div className="account-booking-list">
      {sections.map((section) => (
        <section className="account-booking-group" key={section.id} aria-labelledby={`account-booking-group-${section.id}`}>
          <div className="account-booking-group__heading">
            <h3 id={`account-booking-group-${section.id}`}>{section.title}</h3>
            <span>
              {section.bookings.length} stay{section.bookings.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="account-booking-group__list">{section.bookings.map(renderBooking)}</div>
        </section>
      ))}
    </div>
  );
}

export function AccountPage() {
  const { account, bookings, loading, register, login, logout, updateProfile, cancelBooking, requestPasswordReset, confirmPasswordReset } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get("reset") ?? "";
  const bookingAccountPrefill = (location.state as AccountLocationState)?.bookingAccountPrefill;
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPhone, setRegisterPhone] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState("");
  const [registerOptIn, setRegisterOptIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [passwordHelpOpen, setPasswordHelpOpen] = useState(Boolean(resetToken));
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileOptIn, setProfileOptIn] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [submitting, setSubmitting] = useState<"register" | "login" | "profile" | "logout" | "cancel" | "resetRequest" | "resetConfirm" | null>(null);
  const [cancellingBookingId, setCancellingBookingId] = useState("");

  useEffect(() => {
    if (!account) {
      return;
    }

    setProfileName(account.name);
    setProfilePhone(account.phone);
    setProfileOptIn(account.marketingOptIn);
  }, [account]);

  useEffect(() => {
    if (resetToken) {
      setPasswordHelpOpen(true);
    }
  }, [resetToken]);

  useEffect(() => {
    if (account || !bookingAccountPrefill) {
      return;
    }

    setRegisterName((value) => value || bookingAccountPrefill.name || "");
    setRegisterEmail((value) => value || bookingAccountPrefill.email || "");
    setRegisterPhone((value) => value || bookingAccountPrefill.phone || "");
    setMessage((current) => current ?? { tone: "success", text: "Create an account with the same email to attach your booking request to saved stays." });
  }, [account, bookingAccountPrefill]);

  const onRegister = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting("register");
    setMessage(null);

    if (registerPassword.length < 8) {
      setSubmitting(null);
      setMessage({ tone: "error", text: "Password must be at least 8 characters." });
      return;
    }

    if (registerPassword !== registerPasswordConfirm) {
      setSubmitting(null);
      setMessage({ tone: "error", text: "Passwords do not match." });
      return;
    }

    try {
      await register({
        name: registerName,
        email: registerEmail,
        phone: registerPhone,
        password: registerPassword,
        marketingOptIn: registerOptIn,
      });
      setMessage({ tone: "success", text: "Account created." });
      setRegisterPassword("");
      setRegisterPasswordConfirm("");
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to create account." });
    } finally {
      setSubmitting(null);
    }
  };

  const onLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting("login");
    setMessage(null);

    try {
      await login({ email: loginEmail, password: loginPassword });
      setMessage({ tone: "success", text: "Signed in." });
      setLoginPassword("");
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to sign in." });
    } finally {
      setSubmitting(null);
    }
  };

  const onPasswordResetRequest = async () => {
    setSubmitting("resetRequest");
    setMessage(null);

    try {
      await requestPasswordReset(resetEmail);
      setMessage({ tone: "success", text: "If that email has an account, a password reset link has been sent." });
      setResetEmail("");
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to request a reset link." });
    } finally {
      setSubmitting(null);
    }
  };

  const onPasswordResetConfirm = async () => {
    setSubmitting("resetConfirm");
    setMessage(null);

    if (resetPassword.length < 8) {
      setSubmitting(null);
      setMessage({ tone: "error", text: "Password must be at least 8 characters." });
      return;
    }

    if (resetPassword !== resetPasswordConfirm) {
      setSubmitting(null);
      setMessage({ tone: "error", text: "Passwords do not match." });
      return;
    }

    try {
      await confirmPasswordReset({ token: resetToken, password: resetPassword });
      setResetPassword("");
      setResetPasswordConfirm("");
      setPasswordHelpOpen(false);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete("reset");
        return next;
      }, { replace: true });
      setMessage({ tone: "success", text: "Password updated. You are signed in." });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to reset password." });
    } finally {
      setSubmitting(null);
    }
  };

  const onProfileSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting("profile");
    setMessage(null);

    try {
      await updateProfile({
        name: profileName,
        phone: profilePhone,
        marketingOptIn: profileOptIn,
      });
      setMessage({ tone: "success", text: "Account updated." });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to update account." });
    } finally {
      setSubmitting(null);
    }
  };

  const onLogout = async () => {
    setSubmitting("logout");
    setMessage(null);

    try {
      await logout();
      setMessage({ tone: "success", text: "Signed out." });
    } finally {
      setSubmitting(null);
    }
  };

  const onCancelBooking = async (booking: AccountBooking) => {
    setSubmitting("cancel");
    setCancellingBookingId(booking.id);
    setMessage(null);

    try {
      await cancelBooking(booking.id);
      setMessage({ tone: "success", text: "Cancellation submitted. The dates have been released and cancellation notifications have been queued." });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Unable to cancel booking." });
    } finally {
      setSubmitting(null);
      setCancellingBookingId("");
    }
  };

  return (
    <>
      <SEO
        title="Guest Account | The Deposit Seed House"
        description="Create or manage a guest account for The Deposit Seed House."
        image={siteInfo.exteriorImage}
        canonicalPath="/account"
      />
      <section className="page-hero page-hero--account">
        <Reveal>
          <p className="eyebrow">Guest account</p>
          <h1>{account ? "Your stays at The Seed House." : "Save your details before your next stay."}</h1>
          <p>{account ? account.email : "Create an account or sign in to keep bookings and guest details together."}</p>
          <div className="page-hero__actions">
            <Link className="button button--primary" to="/apartments">
              View apartments
            </Link>
            <Link className="button button--light" to="/contact">
              Contact owner
            </Link>
          </div>
        </Reveal>
      </section>
      <section className="section account-section">
        {message && <StatusMessage tone={message.tone}>{message.text}</StatusMessage>}
        {loading && <div className="account-loading">Loading account...</div>}
        {!loading && !account && (
          <div className="account-auth-grid">
            <Reveal className="account-auth-intro">
              <div>
                <p className="eyebrow">Guest tools</p>
                <h2>Keep booking requests, contact details, and offers in one place.</h2>
              </div>
              <div className="account-auth-benefits" aria-label="Account benefits">
                <span>
                  <ReceiptText size={18} aria-hidden="true" />
                  Saved stay history
                </span>
                <span>
                  <Clock size={18} aria-hidden="true" />
                  Hold status updates
                </span>
                <span>
                  <Tag size={18} aria-hidden="true" />
                  Optional discounts
                </span>
              </div>
            </Reveal>
            <form className="account-card account-form" onSubmit={onRegister}>
              <div className="account-card__heading">
                <UserRound size={24} aria-hidden="true" />
                <div>
                  <p className="eyebrow">New guest</p>
                  <h2>Create account</h2>
                </div>
              </div>
              <label>
                <span>Name</span>
                <input autoComplete="name" maxLength={120} value={registerName} onChange={(event) => setRegisterName(event.target.value)} required />
              </label>
              <label>
                <span>Email</span>
                <input type="email" autoComplete="email" maxLength={254} value={registerEmail} onChange={(event) => setRegisterEmail(event.target.value)} required />
              </label>
              <label>
                <span>Phone</span>
                <input type="tel" autoComplete="tel" maxLength={40} value={registerPhone} onChange={(event) => setRegisterPhone(event.target.value)} />
              </label>
              <label>
                <span>Password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  value={registerPassword}
                  onChange={(event) => setRegisterPassword(event.target.value)}
                  required
                />
              </label>
              <label>
                <span>Confirm password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  value={registerPasswordConfirm}
                  onChange={(event) => setRegisterPasswordConfirm(event.target.value)}
                  required
                />
              </label>
              <label className="account-checkbox">
                <input type="checkbox" checked={registerOptIn} onChange={(event) => setRegisterOptIn(event.target.checked)} />
                <span>
                  <strong>Email me discounts and stay updates</strong>
                  <small>Seasonal rates, openings, and Deposit travel notes.</small>
                </span>
              </label>
              <button className="button button--primary button--full" type="submit" disabled={submitting === "register"}>
                <ShieldCheck size={18} aria-hidden="true" />
                {submitting === "register" ? "Creating" : "Create account"}
              </button>
            </form>

            <div className="account-auth-column">
              <form className="account-card account-form" onSubmit={onLogin}>
                <div className="account-card__heading">
                  <Mail size={24} aria-hidden="true" />
                  <div>
                    <p className="eyebrow">Returning guest</p>
                    <h2>Sign in</h2>
                  </div>
                </div>
                <label>
                  <span>Email</span>
                  <input type="email" autoComplete="email" maxLength={254} value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} required />
                </label>
                <label>
                  <span>Password</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    maxLength={128}
                    value={loginPassword}
                    onChange={(event) => setLoginPassword(event.target.value)}
                    required
                  />
                </label>
                <button className="button button--secondary button--full" type="submit" disabled={submitting === "login"}>
                  {submitting === "login" ? "Signing in" : "Sign in"}
                </button>
                <button
                  className="account-action-link account-action-link--standalone"
                  type="button"
                  onClick={() => {
                    setPasswordHelpOpen((open) => !open);
                    setResetEmail((value) => value || loginEmail);
                  }}
                >
                  Need help signing in?
                </button>
              </form>
              {passwordHelpOpen && !resetToken && (
                <form
                  className="account-card account-form account-reset-panel"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void onPasswordResetRequest();
                  }}
                >
                  <p>Enter your account email and we will send a secure password reset link.</p>
                  <label>
                    <span>Email</span>
                    <input
                      type="email"
                      autoComplete="email"
                      maxLength={254}
                      value={resetEmail}
                      onChange={(event) => setResetEmail(event.target.value)}
                      required
                    />
                  </label>
                  <button className="button button--light button--full" type="submit" disabled={submitting === "resetRequest"}>
                    {submitting === "resetRequest" ? "Sending link" : "Send reset link"}
                  </button>
                </form>
              )}
              {resetToken && (
                <form
                  className="account-card account-form account-reset-panel"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void onPasswordResetConfirm();
                  }}
                >
                  <p>Choose a new password for your guest account.</p>
                  <label>
                    <span>New password</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={128}
                      value={resetPassword}
                      onChange={(event) => setResetPassword(event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Confirm password</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={128}
                      value={resetPasswordConfirm}
                      onChange={(event) => setResetPasswordConfirm(event.target.value)}
                      required
                    />
                  </label>
                  <button className="button button--primary button--full" type="submit" disabled={submitting === "resetConfirm"}>
                    {submitting === "resetConfirm" ? "Updating password" : "Update password"}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {!loading && account && (
          <div className="account-dashboard">
            <AccountDashboardSummary bookings={bookings} marketingOptIn={account.marketingOptIn} />
            <form className="account-card account-form" onSubmit={onProfileSave}>
              <div className="account-card__heading">
                <UserRound size={24} aria-hidden="true" />
                <div>
                  <p className="eyebrow">Profile</p>
                  <h2>{account.name}</h2>
                </div>
              </div>
              <label>
                <span>Name</span>
                <input autoComplete="name" maxLength={120} value={profileName} onChange={(event) => setProfileName(event.target.value)} required />
              </label>
              <label>
                <span>Email</span>
                <span className="account-readonly-field">{account.email}</span>
              </label>
              <label>
                <span>Phone</span>
                <input type="tel" autoComplete="tel" maxLength={40} value={profilePhone} onChange={(event) => setProfilePhone(event.target.value)} />
              </label>
              <label className="account-checkbox">
                <input type="checkbox" checked={profileOptIn} onChange={(event) => setProfileOptIn(event.target.checked)} />
                <span>
                  <strong>Email me discounts and stay updates</strong>
                  <small>Manage Deposit Seed House email offers.</small>
                </span>
              </label>
              <button className="button button--primary button--full" type="submit" disabled={submitting === "profile"}>
                {submitting === "profile" ? "Saving" : "Save account"}
              </button>
              <button className="button button--light button--full account-logout" type="button" onClick={onLogout} disabled={submitting === "logout"}>
                <LogOut size={18} aria-hidden="true" />
                {submitting === "logout" ? "Signing out" : "Sign out"}
              </button>
            </form>

            <section className="account-card account-stays" aria-labelledby="account-stays-title">
              <div className="account-card__heading">
                <CalendarDays size={24} aria-hidden="true" />
                <div>
                  <p className="eyebrow">Bookings</p>
                  <h2 id="account-stays-title">Saved stays</h2>
                </div>
              </div>
              <BookingList bookings={bookings} cancellingBookingId={submitting === "cancel" ? cancellingBookingId : ""} onCancel={onCancelBooking} />
            </section>
          </div>
        )}
      </section>
    </>
  );
}
